import { useAuth } from '@contexts/AuthContext';
import { isStaleParlayDeleteRefusal } from '@parlay-picker/shared/deletionGuards';
import { type DeleteResult, hasPendingParlay } from '@parlay-picker/shared/hooks';
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import { getUserFriendlyError, safeParseJson } from '@utils/api';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_FILTERS,
  DEFAULT_PAGINATION,
  DEFAULT_STATS,
  dbToFrontend,
  serverFilterParams,
} from './tracked-parlays/helpers';
import type {
  ApiParlay,
  FailedLegInfo,
  NewParlay,
  NewParlayLeg,
  PaginationInfo,
  ParlayStats,
  ParlayUpdates,
  TrackedParlay,
  TrackedParlaysFilters,
  TrackParlayResult,
  UseTrackedParlaysResult,
} from './tracked-parlays/types';

// Re-export all public types so callers importing from this file are unaffected
export type {
  FailedLegInfo,
  NewParlay,
  NewParlayLeg,
  OriginFilter,
  PaginationInfo,
  ParlayStats,
  ParlayUpdates,
  ResultFilter,
  ScoreBreakdown,
  TrackedParlay,
  TrackedParlayLeg,
  TrackedParlaysFilters,
  TrackParlayResult,
  UseTrackedParlaysResult,
} from './tracked-parlays/types';

// =============================================================================
// useTrackedParlays Hook
// =============================================================================

/**
 * Hook for managing tracked parlays (distinct from historical bets).
 * Tracked parlays include AI picks, manual entries, and AI-improved parlays.
 */
export function useTrackedParlays(): UseTrackedParlaysResult {
  const { isAuthenticated, authFetch } = useAuth();
  const [trackedParlays, setTrackedParlaysState] = useState<TrackedParlay[]>([]);
  const [stats, setStats] = useState<ParlayStats>(DEFAULT_STATS);
  const [pagination, setPagination] = useState<PaginationInfo>(DEFAULT_PAGINATION);
  const [filters, setFiltersState] = useState<TrackedParlaysFilters>(DEFAULT_FILTERS);
  const filtersRef = useRef<TrackedParlaysFilters>(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Build query string with filters
  const buildQueryString = useCallback(
    (page: number, limit: number, currentFilters: TrackedParlaysFilters) => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        ...serverFilterParams(currentFilters),
      });
      return params.toString();
    },
    [],
  );

  // Load tracked parlays (used for initial load and refetch)
  // Uses filtersRef for default to avoid re-creating on every filter change (prevents double-fetch)
  const loadTrackedParlays = useCallback(
    async (currentFilters?: TrackedParlaysFilters) => {
      const effectiveFilters = currentFilters ?? filtersRef.current;
      setLoading(true);
      setError(null);

      if (isAuthenticated) {
        try {
          // Fetch first page of tracked parlays with stats
          const queryString = buildQueryString(1, 100, effectiveFilters);
          const res = await authFetch(`/api/parlays?${queryString}`);
          if (res.ok) {
            const data = (await res.json()) as {
              parlays?: ApiParlay[];
              stats?: ParlayStats;
              pagination?: PaginationInfo;
              error?: string;
            };
            if (data.error) throw new Error(data.error);

            // Transform to frontend format
            setTrackedParlaysState((data.parlays || []).map(dbToFrontend));
            if (data.stats) setStats(data.stats);
            if (data.pagination) setPagination(data.pagination);
          } else {
            const errorText = `Server returned ${res.status}`;
            setError(errorText);
            setTrackedParlaysState([]);
            setStats(DEFAULT_STATS);
            setPagination(DEFAULT_PAGINATION);
          }
        } catch (err) {
          const errorMsg = getUserFriendlyError(err as Error);
          setError(errorMsg);
          setTrackedParlaysState([]);
          setStats(DEFAULT_STATS);
          setPagination(DEFAULT_PAGINATION);
        }
      } else {
        // Not authenticated - no tracked parlays to show, and the next sign-in starts unfiltered
        filtersRef.current = DEFAULT_FILTERS;
        setFiltersState(DEFAULT_FILTERS);
        setTrackedParlaysState([]);
        setStats(DEFAULT_STATS);
        setPagination(DEFAULT_PAGINATION);
      }

      setLoading(false);
    },
    [isAuthenticated, authFetch, buildQueryString],
  );

  // Set filters and trigger refetch (resets to page 1)
  const setFilters = useCallback(
    (newFilters: TrackedParlaysFilters) => {
      filtersRef.current = newFilters;
      setFiltersState(newFilters);
      // Refetch with new filters (starting from page 1)
      loadTrackedParlays(newFilters);
    },
    [loadTrackedParlays],
  );

  // Refetch all data (e.g., after settlement or tab switch)
  const refetch = useCallback(async () => {
    await loadTrackedParlays();
  }, [loadTrackedParlays]);

  // Load more parlays (next page, append to existing)
  const loadMore = useCallback(async () => {
    if (!isAuthenticated || !pagination.hasMore || loadingMore) return;

    setLoadingMore(true);

    try {
      const nextPage = pagination.page + 1;
      const queryString = buildQueryString(nextPage, 100, filtersRef.current);
      const res = await authFetch(`/api/parlays?${queryString}`);
      if (res.ok) {
        const data = (await res.json()) as {
          parlays?: ApiParlay[];
          stats?: ParlayStats;
          pagination?: PaginationInfo;
          error?: string;
        };
        if (data.error) throw new Error(data.error);

        // Append new parlays to existing list
        const newParlays = (data.parlays || []).map(dbToFrontend);
        setTrackedParlaysState((prev) => [...prev, ...newParlays]);
        if (data.stats) setStats(data.stats);
        if (data.pagination) setPagination(data.pagination);
      } else {
        setPagination((prev) => ({ ...prev, hasMore: false }));
      }
    } catch (_err) {
      // Stop trying to load more on error to prevent infinite retries
      setPagination((prev) => ({ ...prev, hasMore: false }));
    } finally {
      setLoadingMore(false);
    }
  }, [
    isAuthenticated,
    authFetch,
    buildQueryString,
    pagination.hasMore,
    pagination.page,
    loadingMore,
  ]);

  // Load tracked parlays on mount or auth change
  useEffect(() => {
    loadTrackedParlays();
  }, [loadTrackedParlays]);

  // Catch-up refetch when the tab regains focus.
  // The only automatic post-settlement refresh is a one-shot `settlement_complete`
  // SSE broadcast (no replay), delivered solely while the live-stats SSE is
  // connected. If a game settles while the tab is hidden/asleep (e.g. overnight),
  // that broadcast is missed, and the nested legs render stale (no results, no
  // progress bars) until a manual reload. Re-pull whenever the tab becomes
  // visible while any parlay is still pending, so settlement is picked up.
  const trackedParlaysRef = useRef(trackedParlays);
  trackedParlaysRef.current = trackedParlays;
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (hasPendingParlay(trackedParlaysRef.current)) {
        void loadTrackedParlays();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadTrackedParlays]);

  // Track a new parlay (from any origin: ai, manual, or bet)
  const trackParlay = useCallback(
    async (parlayData: NewParlay): Promise<TrackParlayResult> => {
      if (!isAuthenticated) {
        return {
          success: false,
          parlay: null,
          error: 'Must be logged in to track parlays',
        };
      }

      // Validate: all legs must have gameId for settlement/live stats to work
      const legsWithoutGameId = parlayData.legs.filter((leg) => !leg.gameId);

      if (legsWithoutGameId.length > 0) {
        // Build descriptive error message
        const legProp = (leg: NewParlayLeg): string =>
          `${leg.overUnder || ''} ${leg.line ?? ''} ${formatStatLabel(leg.statType ?? '')}`.trim();
        const legDescriptions = legsWithoutGameId
          .map((leg) => `${leg.player || leg.team || 'Unknown'} ${legProp(leg)}`.trim())
          .join(', ');

        // Build hallucination detection context
        // Compare failed legs' teams to teams in healthy legs (which have valid gameIds)
        const healthyLegsForContext = parlayData.legs.filter((leg) => leg.gameId);
        const teamsWithValidGames = new Set(
          healthyLegsForContext.flatMap((leg) => [leg.team, leg.opponentTeam].filter(Boolean)),
        );

        // Fire-and-forget POST to server for persistent tracking
        fetch('/api/tracking-errors', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'missing_game_id',
            tier: parlayData.tier,
            sportsbook: parlayData.sportsbook,
            // Hallucination detection data
            failedLegs: legsWithoutGameId.map((leg) => ({
              player: leg.player,
              team: leg.team,
              statType: leg.statType,
              line: leg.line,
              overUnder: leg.overUnder,
              opponentTeam: leg.opponentTeam,
              teamInValidGames: leg.team ? teamsWithValidGames.has(leg.team) : false,
            })),
            // Context for analysis
            selectedGameIds: parlayData.selectedGameIds,
            teamsWithValidGames: Array.from(teamsWithValidGames),
            allLegsCount: parlayData.legs.length,
            healthyLegsCount: healthyLegsForContext.length,
            timestamp: new Date().toISOString(),
          }),
        }).catch(() => {}); // Silently ignore errors

        // Return error with healthy legs for recovery
        const healthyLegs = parlayData.legs.filter((leg) => leg.gameId);

        return {
          success: false,
          parlay: null,
          error: `Cannot track: ${legsWithoutGameId.length} leg(s) missing game data: ${legDescriptions}`,
          recoveryData:
            healthyLegs.length > 0
              ? {
                  healthyLegs,
                  failedLegs: legsWithoutGameId.map(
                    (leg): FailedLegInfo => ({
                      player: leg.player,
                      team: leg.team,
                      prop: legProp(leg),
                    }),
                  ),
                }
              : undefined,
        };
      }

      try {
        const res = await authFetch('/api/parlays/track', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: parlayData.name || parlayData.tier,
            tier: parlayData.tier,
            totalOdds: parlayData.totalOdds,
            confidence: parlayData.confidence,
            reasoning: parlayData.reasoning,
            stake: parlayData.stake,
            origins: parlayData.origins || ['ai'],
            isAIImproved: parlayData.isAIImproved || false,
            aiAnalysisVerdict: parlayData.aiAnalysisVerdict,
            scoreReliability: parlayData.scoreReliability ?? undefined,
            selectedGameIds: parlayData.selectedGameIds, // Games selected when AI generated picks
            sportsbook: parlayData.sportsbook, // Which sportsbook's lines were used
            generationId: parlayData.generationId, // Links to pick_predictions for config analysis
            parlayIndex: parlayData.parlayIndex, // Which parlay in the generation
            analysisConfig: parlayData.analysisConfig, // For factor flags + ML enrichment
            // Scores locked at track time
            trackedStrategyScore: parlayData.trackedStrategyScore,
            trackedPredictionScore: parlayData.trackedPredictionScore,
            trackedStrategyRec: parlayData.trackedStrategyRec,
            trackedPredictionRec: parlayData.trackedPredictionRec,
            trackedScoreBreakdown: parlayData.trackedScoreBreakdown,
            league: parlayData.league, // LeagueKey or comma-separated for multi-league
            legs: parlayData.legs.map((leg) => ({
              player: leg.player,
              playerId: leg.playerId,
              team: leg.team,
              opponentTeam: leg.opponentTeam, // Opponent team for matchup display
              // Pass all prop fields - no extraction/parsing needed
              prop: leg.prop, // Full display string
              statType: leg.statType, // Normalized stat type
              marketKey: leg.marketKey, // Settlement routes team totals by it
              line: leg.line, // Numeric line (null for moneyline)
              overUnder: leg.overUnder, // Over/Under (null for spreads/moneyline)
              odds: leg.odds,
              gameId: leg.gameId,
              gameDate: leg.gameDate,
              gameStartsAt: leg.gameStartsAt, // The server's started-game check falls back to it
              betContext: leg.betContext, // ML training data
              aiEdge: leg.aiEdge,
              // Phase 4: Fatigue & game context
              isB2b: leg.isB2b,
              blowoutRisk: leg.blowoutRisk,
              isHomeGame: leg.isHomeGame ?? null,
              // Per-leg config snapshot (captured when leg was added to builder)
              analysisConfig: leg.analysisConfig ?? null,
              leagueKey: leg.leagueKey,
              provenance: leg.provenance,
              predictionId: leg.predictionId,
              factorsUsed: leg.factorsUsed ?? undefined,
            })),
          }),
        });

        // Parse the response
        const data = (await res.json()) as {
          parlay?: ApiParlay;
          quota?: import('@parlay-picker/shared/quotaCoin').QuotaCoinData;
          error?: string;
          action?: string;
          // 429 tracking quota fields (spread from QuotaCheckResult)
          used?: number;
          limit?: number;
          remaining?: number;
          resetsAt?: string;
        };

        // Handle error response
        if (!res.ok || data.error) {
          const errorMessage = data.error || `Failed to track parlay (${res.status})`;
          // Extract quota info from 429 tracking limit responses
          const quotaInfo =
            typeof data.used === 'number' && typeof data.limit === 'number'
              ? {
                  used: data.used,
                  limit: data.limit,
                  remaining: typeof data.remaining === 'number' ? data.remaining : 0,
                  resetsAt: typeof data.resetsAt === 'string' ? data.resetsAt : '',
                }
              : undefined;
          return {
            success: false,
            parlay: null,
            error: errorMessage,
            quotaInfo,
          };
        }

        // Success - parse and return the new parlay
        if (!data.parlay) {
          return {
            success: false,
            parlay: null,
            error: 'No parlay returned from server',
          };
        }
        const newParlay = dbToFrontend(data.parlay);
        if (Object.keys(serverFilterParams(filtersRef.current)).length > 0) {
          // Only the server knows if the new parlay matches; its stats follow origin and sportsbook too
          void loadTrackedParlays();
        } else {
          setTrackedParlaysState((prev) => [newParlay, ...prev]);
          setStats((prev) => ({ ...prev, total: prev.total + 1, pending: prev.pending + 1 }));
          setPagination((prev) => ({ ...prev, total: prev.total + 1 }));
        }
        return { success: true, parlay: newParlay, error: null, quotaData: data.quota ?? null };
      } catch (err) {
        const errorMessage = getUserFriendlyError(err as Error);
        return { success: false, parlay: null, error: errorMessage };
      }
    },
    [isAuthenticated, authFetch, loadTrackedParlays],
  );

  // Update a tracked parlay (e.g., mark result)
  const updateParlay = useCallback(
    async (parlayId: number, updates: ParlayUpdates): Promise<boolean> => {
      if (!isAuthenticated) return false;

      try {
        const res = await authFetch(`/api/parlays/${parlayId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            result: updates.result,
            stake: updates.stake,
            actualPayout: updates.payout,
            settledAt: updates.result !== 'pending' ? new Date().toISOString() : null,
          }),
        });

        if (res.ok) {
          setTrackedParlaysState((prev) =>
            prev.map((p) => (p.id === parlayId ? { ...p, ...updates } : p)),
          );
          return true;
        }
        return false;
      } catch (_err) {
        return false;
      }
    },
    [isAuthenticated, authFetch],
  );

  // Remove a tracked parlay
  const removeParlay = useCallback(
    async (parlayId: number): Promise<DeleteResult> => {
      if (!isAuthenticated) {
        return { success: false, error: 'Must be logged in to delete parlays' };
      }

      try {
        const res = await authFetch(`/api/parlays/${parlayId}`, {
          method: 'DELETE',
        });

        if (res.ok) {
          const removed = trackedParlays.find((p) => p.id === parlayId);
          setTrackedParlaysState((prev) => prev.filter((p) => p.id !== parlayId));
          if (removed) {
            setStats((prev) => {
              const next = { ...prev, total: prev.total - 1 };
              if (removed.result === 'won') next.won = prev.won - 1;
              else if (removed.result === 'lost') next.lost = prev.lost - 1;
              else if (removed.result === 'void') next.voided = prev.voided - 1;
              else next.pending = prev.pending - 1;
              return next;
            });
            setPagination((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
          }
          return { success: true };
        }

        const body = await safeParseJson<{ error?: string }>(res);
        const error = res.status < 500 && body.error ? body.error : 'Failed to delete parlay';
        if (isStaleParlayDeleteRefusal(res.status)) {
          void loadTrackedParlays();
        }
        return { success: false, error };
      } catch (err) {
        return { success: false, error: getUserFriendlyError(err as Error) };
      }
    },
    [isAuthenticated, authFetch, trackedParlays, loadTrackedParlays],
  );

  return {
    trackedParlays,
    stats,
    pagination,
    filters,
    setFilters,
    trackParlay,
    updateParlay,
    removeParlay,
    loadMore,
    refetch,
    loading,
    loadingMore,
    error,
  };
}
